import { getConfig, isConfigAvailable } from '@barbanet/api-service'

import { computed, ref } from 'vue'

import { useExceptionHandler } from '@barbanet/ui-kit/composables'

import { userPermissionsService } from '../services/permission'
import { usePermissionStore } from '../stores/permission-store'

let _permissionInstance: Nullable<ReturnType<typeof createPermissionInstance>> =
	null

function createPermissionInstance() {
	const store = usePermissionStore()

	const permissionSet = computed(() => new Set(store.permissions))

	const has = (key: string) => {
		return computed(() => permissionSet.value.has(key))
	}

	const hasPermission = (expression: string) => {
		if (!expression) {
			return computed(() => true)
		}

		const normalized = expression
			.replace(/\|\|/g, ' || ')
			.replace(/&&/g, ' && ')
			.trim()

		const tokens = normalized.split(/\s+/)

		const evaluate = () => {
			const stack: (boolean | string)[] = []
			for (const token of tokens) {
				if (
					token === '&&' ||
					token === '||' ||
					token === '(' ||
					token === ')'
				) {
					stack.push(token)
				} else {
					stack.push(has(token).value)
				}
			}

			try {
				return eval(stack.join(' '))
			} catch {
				return false
			}
		}

		return computed(evaluate)
	}

	const syncing = ref(false)
	const sync = async () => {
		if (!isConfigAvailable() || !getConfig().credentials?.accessToken) {
			return
		}

		syncing.value = true
		try {
			const response = await userPermissionsService()

			if (response && response.data) {
				await store.setPermissions(response.data.content.authorities)
			}
		} catch (error) {
			useExceptionHandler(error)
		} finally {
			syncing.value = false
		}
	}

	const clear = async () => {
		await store.clearPermissions()
	}

	return {
		permissions: permissionSet,
		hasPermission,
		has,
		sync,
		syncing,
		clear
	}
}

export function usePermission() {
	if (!_permissionInstance) {
		_permissionInstance = createPermissionInstance()
	}

	return _permissionInstance
}
